import { BillingPricingMode } from "@api/shared-runtime/enums";
import type { RouterOutputs } from "@/trpc";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  formatPlanDate,
  formatPlanInstant,
  getLastServiceDate,
} from "@/components/billing-plan/billing-plan-values";
import {
  SAILE_CHARGES_LABEL,
  WAGE_PASS_THROUGH_DETAIL,
  WAGE_PASS_THROUGH_LABEL,
} from "@/components/billing-plan/wage-pass-through-copy";
import {
  formatUsageBillingCurrency,
  formatUsageBillingQuantity,
  getUsageBillingAllowanceLabel,
  isUsageBillingCategoryUnlimited,
} from "./usage-billing-format";
import { UsageBillingCategoryUsage } from "./usage-billing-category-usage";
import { USAGE_BILLING_COLUMNS } from "./usage-billing-columns";
import {
  formatUsageBillingDateTime,
  formatUsageBillingMonthLabel,
} from "./usage-billing-time";

type UsageSummary =
  RouterOutputs["adminUsageBilling"]["getUnitDetail"]["summary"];
type BillingCycle = UsageSummary["cycles"][number];
type CycleCategory = BillingCycle["categories"][number];

export function UsageBillingCycles({
  summary,
  monthKey,
}: {
  summary: UsageSummary;
  monthKey: string;
}) {
  return (
    <section aria-label="Billing cycles" className="space-y-4 border-b p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-semibold">
            Cycles starting in {formatUsageBillingMonthLabel(monthKey)}
          </h3>
          <p className="text-sm text-muted-foreground">
            Each cycle includes its full base fee, allowance, and recorded usage
            through its service end. No proration applies.
          </p>
        </div>
        <div className="text-right">
          <p className="text-xs text-muted-foreground">Cycle totals</p>
          <p className="font-semibold">
            {formatUsageBillingCurrency(summary.totalAmount)}
          </p>
        </div>
      </div>
      {summary.cycles.length === 0 && (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            No billing cycles start in this month. Calendar-month events appear
            separately below.
          </p>
          <UsageBillingCategorySummary categories={summary.categories} />
        </div>
      )}
      {summary.cycles.map((cycle) => (
        <UsageBillingCycle
          key={`${cycle.plan?.id}-${cycle.periodStart}`}
          cycle={cycle}
        />
      ))}
    </section>
  );
}

function UsageBillingCycle({ cycle }: { cycle: BillingCycle }) {
  const startsOn = formatPlanInstant(cycle.periodStart);
  return (
    <section
      className="space-y-3 rounded-lg border bg-white p-4"
      aria-label={`Billing cycle starting ${startsOn}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h4 className="font-medium">
            {startsOn} – {formatPlanDate(getLastServiceDate(cycle.periodEnd))}
          </h4>
          <p className="text-xs text-muted-foreground">
            Usage from {formatUsageBillingDateTime(cycle.periodStart)} until{" "}
            {formatUsageBillingDateTime(cycle.periodEnd)}
          </p>
          <p className="text-xs text-muted-foreground">
            Monthly renewal date: {formatPlanDate(cycle.renewalDate)}
          </p>
        </div>
        <div className="text-right">
          <p className="text-xs text-muted-foreground">Cycle total</p>
          <p className="font-semibold">
            {formatUsageBillingCurrency(cycle.totalAmount)}
          </p>
        </div>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Category</TableHead>
            <TableHead>Used</TableHead>
            <TableHead>Included</TableHead>
            <TableHead>Overage</TableHead>
            <TableHead>Rate</TableHead>
            <TableHead className="text-right">Charge</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {cycle.categories.map((category) => (
            <TableRow key={category.categoryCode}>
              <TableCell>{category.displayName}</TableCell>
              <TableCell>
                {formatUsageBillingQuantity(category.usedQuantity)}
              </TableCell>
              <TableCell>{getUsageBillingAllowanceLabel(category)}</TableCell>
              <TableCell>
                {isUsageBillingCategoryUnlimited(category) ||
                category.billableQuantity === null
                  ? "—"
                  : formatUsageBillingQuantity(category.billableQuantity)}
              </TableCell>
              <TableCell>{getCycleRateLabel(category)}</TableCell>
              <TableCell className="text-right">
                {category.lineAmount === null
                  ? "Not billed"
                  : formatUsageBillingCurrency(category.lineAmount)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TableCell colSpan={5} className="text-muted-foreground">
              Base fee
            </TableCell>
            <TableCell className="text-right tabular-nums">
              {formatUsageBillingCurrency(cycle.baseMonthlyAmount)}
            </TableCell>
          </TableRow>
          {cycle.wagePassThroughAmount !== null && (
            <>
              <TableRow>
                <TableCell colSpan={5} className="text-muted-foreground">
                  {SAILE_CHARGES_LABEL}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatUsageBillingCurrency(cycle.saileChargesAmount)}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell colSpan={5}>
                  {WAGE_PASS_THROUGH_LABEL}{" "}
                  <span className="text-xs text-muted-foreground">
                    {WAGE_PASS_THROUGH_DETAIL}
                  </span>
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatUsageBillingCurrency(cycle.wagePassThroughAmount)}
                </TableCell>
              </TableRow>
            </>
          )}
          <TableRow>
            <TableCell colSpan={5} className="font-semibold">
              Cycle total
            </TableCell>
            <TableCell className="text-right font-semibold tabular-nums">
              {formatUsageBillingCurrency(cycle.totalAmount)}
            </TableCell>
          </TableRow>
        </TableFooter>
      </Table>
    </section>
  );
}

function UsageBillingCategorySummary({
  categories,
}: {
  categories: UsageSummary["categories"];
}) {
  const categoriesByCode = new Map(
    categories.map((category) => [category.categoryCode, category]),
  );
  const columnsByCode = new Map(
    USAGE_BILLING_COLUMNS.map((column) => [column.categoryCode, column]),
  );
  for (const category of categories) {
    columnsByCode.set(category.categoryCode, {
      categoryCode: category.categoryCode,
      columnLabel: category.displayName,
    });
  }
  return (
    <section
      aria-label="Category usage"
      className="rounded-lg border bg-white p-4"
    >
      <h4 className="mb-3 text-sm font-medium">Category usage</h4>
      <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
        {Array.from(columnsByCode.values()).map((column) => (
          <div key={column.categoryCode} className="text-right">
            <dt className="text-xs text-muted-foreground">
              {column.columnLabel}
            </dt>
            <dd>
              <UsageBillingCategoryUsage
                category={categoriesByCode.get(column.categoryCode)}
              />
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function getCycleRateLabel(category: CycleCategory): string {
  if (
    category.pricingMode === BillingPricingMode.PercentOfBasis &&
    category.basisPercent !== null
  ) {
    return `${Number(category.basisPercent)}% of wages`;
  }
  if (
    category.pricingMode === BillingPricingMode.PerUnit &&
    category.unitAmount !== null
  ) {
    return `${formatUsageBillingCurrency(category.unitAmount)} / ${category.unitNoun}`;
  }
  return "Not billed";
}
